import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import NewsCard from "@/components/NewsCard";
import AdSlot from "@/components/AdSlot";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isPremium = user && user !== false && user.subscription_tier === "premium";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/articles?limit=60");
        setArticles(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="grid gap-6 md:grid-cols-12">
        <Skeleton className="md:col-span-8 h-96" />
        <div className="md:col-span-4 space-y-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  if (!articles.length) {
    return (
      <div className="text-center py-24 max-w-md mx-auto" data-testid="home-empty-state">
        <div className="text-6xl mb-4">📰</div>
        <h2 className="font-kannada-heading text-3xl">ಸುದ್ದಿಗಳು ಶೀಘ್ರದಲ್ಲೇ...</h2>
        <p className="text-muted-foreground mt-3 font-kannada-body">
          ಇನ್ನೂ ಯಾವುದೇ ಸುದ್ದಿಗಳಿಲ್ಲ. ಅಡ್ಮಿನ್ ಪ್ಯಾನೆಲ್‌ಗೆ ಹೋಗಿ ಹೊಸ ಸುದ್ದಿ ಸೇರಿಸಿ.
        </p>
      </div>
    );
  }

  const featured = articles.filter((a) => a.is_featured);
  // Newest first — hero is always the most recently created article,
  // even if none are flagged featured. Top-stories sidebar prefers featured
  // items but falls back to the next newest ones.
  const hero = articles[0];
  const heroId = hero?.id;
  const secondaryFeatured = featured.filter((a) => a.id !== heroId).slice(0, 3);
  const secondaryPad = articles.filter((a) => a.id !== heroId && !secondaryFeatured.some((s) => s.id === a.id));
  const secondary = [...secondaryFeatured, ...secondaryPad].slice(0, 3);
  const remaining = articles.filter((a) => a.id !== heroId && !secondary.some((s) => s.id === a.id));

  return (
    <div className="space-y-12">
      {/* Hero bento */}
      <section className="grid gap-6 md:grid-cols-12" data-testid="hero-section">
        <div className="md:col-span-8">{hero && <NewsCard article={hero} variant="hero" />}</div>
        <aside className="md:col-span-4 space-y-4" data-testid="hero-sidebar">
          <div className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground border-b border-border pb-2">
            ಟಾಪ್ ಸ್ಟೋರೀಸ್
          </div>
          {secondary.map((a, i) => (
            <NewsCard key={a.id} article={a} variant="compact" index={i} />
          ))}
        </aside>
      </section>

      {/* Ad between hero and latest grid */}
      {!isPremium && <AdSlot slot="home_middle" className="h-24 md:h-28" />}

      {/* Latest grid */}
      <section>
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-kannada-heading text-3xl">ಇತ್ತೀಚಿನ ಸುದ್ದಿಗಳು</h2>
          <span className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground">
            Latest updates
          </span>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="latest-grid">
          {remaining.slice(0, 9).map((a, i) => (
            <NewsCard key={a.id} article={a} index={i} />
          ))}
        </div>
      </section>

      {/* Trending strip */}
      {remaining.length > 9 && (
        <section>
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-kannada-heading text-3xl">ಇನ್ನಷ್ಟು</h2>
            <span className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground">
              More stories
            </span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {remaining.slice(9, 15).map((a, i) => (
              <NewsCard key={a.id} article={a} variant="compact" index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
